import type { Item, ItemId, PersonId } from '../types.js';
import { resolvePersonSet, type ItemSelector, type SplitContext, type SplitOp } from './dsl.js';

export interface Share {
  personId: PersonId;
  weight: number;
}

/** Who owes what fraction of each item. Items absent from the map are unclaimed. */
export type Assignments = ReadonlyMap<ItemId, Share[]>;

export interface ResolveResult {
  assignments: Assignments;
  unassigned: ItemId[];
}

/**
 * Apply operations in sequence to produce a final assignment.
 *
 * Later operations override earlier ones for the items they touch. That
 * ordering is what makes the natural phrasing work: "we're all splitting
 * everything, but Jason had the cigarettes" is two ops, and the second simply
 * wins on the item it names.
 */
export function resolveOps(ops: SplitOp[], context: SplitContext): ResolveResult {
  const assignments = new Map<ItemId, Share[]>();

  const select = (selector: ItemSelector): Item[] => {
    switch (selector.kind) {
      case 'ids': {
        const wanted = new Set(selector.ids);
        return context.items.filter((item) => wanted.has(item.id));
      }
      case 'category':
        // "the food" in everyday speech means every dish. Meat and dessert are
        // sub-kinds of food, not alternatives to it; only "food" widens.
        return context.items.filter((item) =>
          selector.category === 'food'
            ? item.category === 'food' || item.category === 'meat' || item.category === 'dessert'
            : item.category === selector.category,
        );
      case 'rest':
        // Evaluated now, not up front: "everything else" means everything
        // still unclaimed at this point in the sentence.
        return context.items.filter((item) => (assignments.get(item.id)?.length ?? 0) === 0);
    }
  };

  for (const op of ops) {
    const targets = select(op.items);

    if (op.kind === 'assign') {
      const ids = resolvePersonSet(op.to, context.people);
      if (ids.length === 0) continue;

      // Weights are given against the caller's ordering of `to.ids`, but
      // resolvePersonSet returns roster order — map through the original list
      // so a weight always follows its person.
      const weightFor = new Map<PersonId, number>();
      if (op.weights) {
        op.to.ids.forEach((id, index) => weightFor.set(id, op.weights?.[index] ?? 1));
      }

      const shares = ids.map((personId) => ({
        personId,
        weight: weightFor.get(personId) ?? 1,
      }));
      for (const item of targets) assignments.set(item.id, shares.map((s) => ({ ...s })));
      continue;
    }

    const removed = new Set(resolvePersonSet(op.from, context.people));
    for (const item of targets) {
      const current = assignments.get(item.id);
      if (!current) continue;
      assignments.set(
        item.id,
        current.filter((share) => !removed.has(share.personId)),
      );
    }
  }

  const unassigned = context.items
    .filter((item) => (assignments.get(item.id)?.length ?? 0) === 0)
    .map((item) => item.id);

  return { assignments, unassigned };
}

export interface Clarification {
  itemId: ItemId;
  itemName: string;
  question: string;
}

/**
 * Questions for whatever the sentence left out.
 *
 * Generated here rather than by the model: the set of unclaimed items is a fact
 * we already know exactly, so asking a model to rediscover it would be slower,
 * costlier and less reliable. This is the "if he doesn't say, the AI should
 * ask him" behaviour, and it never needs an inference call.
 */
export function clarifications(result: ResolveResult, items: Item[]): Clarification[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  return result.unassigned.flatMap((itemId) => {
    const item = byId.get(itemId);
    if (!item) return [];
    return [{ itemId, itemName: item.name, question: `Who had the ${item.name}?` }];
  });
}
